const DEFAULT_AUTOPLAY_DELAY = 4000;
const HOVER_GRACE_MS = 150;
const SWIPE_AXIS_LOCK_PX = 8;
const SWIPE_THRESHOLD_PX = 48;

class SybilHero extends HTMLElement {
  connectedCallback() {
    if (this.abortController) return;

    this.abortController = new AbortController();
    this.slides = [...this.querySelectorAll('[data-sybil-slide]')];
    this.dots = [...this.querySelectorAll('[data-sybil-dot]')];
    this.currentIndex = Math.max(
      0,
      this.slides.findIndex((slide) => slide.getAttribute('aria-hidden') === 'false'),
    );
    this.pauseReasons = new Set();
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.tray = this.parentElement?.querySelector('[data-sybil-hotspot-tray]');
    const configuredDelay = Number(this.dataset.sybilAutoplayDelay);
    this.autoplayDelay =
      Number.isFinite(configuredDelay) && configuredDelay >= 1000
        ? configuredDelay
        : DEFAULT_AUTOPLAY_DELAY;

    const { signal } = this.abortController;
    this.querySelector('[data-sybil-previous]')?.addEventListener(
      'click',
      () => this.show(this.currentIndex - 1, true),
      { signal },
    );
    this.querySelector('[data-sybil-next]')?.addEventListener(
      'click',
      () => this.show(this.currentIndex + 1, true),
      { signal },
    );
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.show(index, true), { signal });
    });

    this.addEventListener(
      'pointerenter',
      (event) => {
        if (event.pointerType === 'touch') return;
        this.pause('pointer');
      },
      { signal },
    );
    this.addEventListener(
      'pointerleave',
      (event) => {
        if (event.pointerType === 'touch') return;
        this.resume('pointer');
      },
      { signal },
    );
    this.addEventListener(
      'focusin',
      (event) => {
        // Touch and mouse taps focus the dots/arrows without :focus-visible, and their
        // focus lingers after the tap — only keyboard-visible focus should hold the pause.
        if (!event.target.matches(':focus-visible')) return;
        this.pause('focus');
      },
      { signal },
    );
    this.addEventListener(
      'focusout',
      () => {
        requestAnimationFrame(() => {
          if (!this.contains(document.activeElement)) this.resume('focus');
        });
      },
      { signal },
    );
    this.motionQuery.addEventListener('change', () => this.handleMotionChange(), {
      signal,
    });
    this.addEventListener('shopify:block:select', (event) => this.selectEditorBlock(event), {
      signal,
    });
    this.addEventListener('shopify:block:deselect', () => this.resume('editor'), {
      signal,
    });

    this.setupHotspots(signal);
    this.setupSwipe(signal);
    window.addEventListener('resize', () => this.handleHotspotResize(), { signal });
    this.show(this.currentIndex, false);
    this.schedule();
  }

  disconnectedCallback() {
    window.clearTimeout(this.timer);
    window.clearTimeout(this.hotspotHideTimer);
    this.restoreTrayPanel();
    this.abortController?.abort();
    this.abortController = undefined;
  }

  show(index, resetTimer = true) {
    if (!this.slides.length) return;

    this.closeAllHotspots();
    this.currentIndex = (index + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === this.currentIndex;
      slide.setAttribute('aria-hidden', String(!isActive));
      slide.toggleAttribute('inert', !isActive);
      slide.classList.toggle('is-active', isActive);
    });
    this.dots.forEach((dot, dotIndex) => {
      const isActive = dotIndex === this.currentIndex;
      dot.setAttribute('aria-current', String(isActive));
      dot.classList.toggle('is-active', isActive);
    });

    if (resetTimer) this.schedule();
  }

  schedule() {
    window.clearTimeout(this.timer);
    if (
      this.slides.length < 2 ||
      this.motionQuery.matches ||
      this.pauseReasons.size > 0 ||
      !this.isConnected
    ) {
      return;
    }

    this.timer = window.setTimeout(() => {
      this.show(this.currentIndex + 1, false);
      this.schedule();
    }, this.autoplayDelay);
  }

  pause(reason) {
    this.pauseReasons.add(reason);
    window.clearTimeout(this.timer);
  }

  resume(reason) {
    this.pauseReasons.delete(reason);
    this.schedule();
  }

  handleMotionChange() {
    this.toggleAttribute('data-reduced-motion', this.motionQuery.matches);
    this.schedule();
  }

  selectEditorBlock(event) {
    const selectedBlock = event.target.closest('[data-sybil-slide]');
    const index = this.slides.indexOf(selectedBlock);
    if (index < 0) return;

    this.pause('editor');
    this.show(index, false);
  }

  closeAllHotspots() {
    window.clearTimeout(this.hotspotHideTimer);
    this.hotspots?.forEach((hotspot) => {
      hotspot.dataset.locked = 'false';
      this.closeHotspot(hotspot);
    });
    this.restoreTrayPanel();
  }

  isMobileHotspotLayout() {
    return window.innerWidth <= 749;
  }

  scheduleHotspotClose(hotspot) {
    window.clearTimeout(this.hotspotHideTimer);
    this.hotspotHideTimer = window.setTimeout(() => {
      if (hotspot.dataset.locked !== 'true') this.closeHotspot(hotspot);
    }, HOVER_GRACE_MS);
  }

  movePanelToTray(hotspot, panel) {
    if (!(this.tray instanceof HTMLElement) || !(panel instanceof HTMLElement)) return;

    this.restoreTrayPanel();
    panel.sybilOriginalParent = hotspot;
    this.tray.append(panel);
    this.trayPanel = panel;
    void this.tray.offsetHeight;
    this.tray.classList.add('is-open');
  }

  restoreTrayPanel() {
    if (!(this.tray instanceof HTMLElement)) return;

    this.tray.classList.remove('is-open');
    const panel = this.trayPanel ?? this.tray.querySelector('[data-sybil-hotspot-panel]');
    if (panel instanceof HTMLElement && panel.sybilOriginalParent instanceof HTMLElement) {
      panel.sybilOriginalParent.append(panel);
      panel.sybilOriginalParent = undefined;
    }
    this.trayPanel = undefined;
  }

  positionHotspotPanel(hotspot) {
    const panel = hotspot.querySelector('[data-sybil-hotspot-panel]');
    const frame = hotspot.closest('.sybil-hero__frame');
    if (
      this.isMobileHotspotLayout() ||
      !(panel instanceof HTMLElement) ||
      !(frame instanceof HTMLElement)
    ) {
      return;
    }

    const panelBounds = panel.getBoundingClientRect();
    const frameBounds = frame.getBoundingClientRect();
    const hotspotBounds = hotspot.getBoundingClientRect();
    const panelSide =
      panelBounds.left < frameBounds.left
        ? 'left'
        : panelBounds.right > frameBounds.right
          ? 'right'
          : hotspot.dataset.panelSide;
    if (panelSide) hotspot.dataset.panelSide = panelSide;
    hotspot.dataset.panelVertical =
      hotspotBounds.top + hotspotBounds.height / 2 > window.innerHeight / 2 ? 'top' : 'bottom';
  }

  handleHotspotResize() {
    if (!this.isMobileHotspotLayout()) this.restoreTrayPanel();
    this.hotspots?.forEach((hotspot) => {
      if (hotspot.dataset.open === 'true') this.positionHotspotPanel(hotspot);
    });
  }

  setupSwipe(signal) {
    // pan-y keeps vertical scrolling native; horizontal moves keep firing pointer events.
    this.style.touchAction = 'pan-y pinch-zoom';

    this.addEventListener(
      'pointerdown',
      (event) => {
        if (event.pointerType === 'mouse' || !event.isPrimary) return;
        this.swipePointerId = event.pointerId;
        this.swipeStartX = event.clientX;
        this.swipeStartY = event.clientY;
        this.swipeAxis = null;
      },
      { signal },
    );

    this.addEventListener(
      'pointermove',
      (event) => {
        if (event.pointerId !== this.swipePointerId || this.swipeAxis) return;
        const deltaX = event.clientX - this.swipeStartX;
        const deltaY = event.clientY - this.swipeStartY;
        if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) < SWIPE_AXIS_LOCK_PX) return;
        this.swipeAxis = Math.abs(deltaX) > Math.abs(deltaY) ? 'x' : 'y';
        // Capturing retargets the rest of the gesture (and its click) to the host, so a
        // horizontal drag ending over a dot, arrow, or hotspot trigger cannot activate it.
        if (this.swipeAxis === 'x') this.setPointerCapture(event.pointerId);
      },
      { signal },
    );

    this.addEventListener(
      'pointerup',
      (event) => {
        if (event.pointerId !== this.swipePointerId) return;
        const deltaX = event.clientX - this.swipeStartX;
        const axis = this.swipeAxis;
        this.swipePointerId = undefined;
        this.swipeAxis = null;
        if (axis !== 'x' || Math.abs(deltaX) < SWIPE_THRESHOLD_PX) return;
        this.show(this.currentIndex + (deltaX < 0 ? 1 : -1), true);
      },
      { signal },
    );

    this.addEventListener(
      'pointercancel',
      (event) => {
        if (event.pointerId !== this.swipePointerId) return;
        this.swipePointerId = undefined;
        this.swipeAxis = null;
      },
      { signal },
    );
  }

  setupHotspots(signal) {
    this.hotspots = [...this.querySelectorAll('[data-sybil-hotspot]')];

    this.hotspots.forEach((hotspot) => {
      const trigger = hotspot.querySelector('[data-sybil-hotspot-trigger]');
      const panel = hotspot.querySelector('[data-sybil-hotspot-panel]');
      if (!trigger || !panel) return;
      hotspot.sybilPanel = panel;

      hotspot.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'touch' || this.isMobileHotspotLayout()) return;
        window.clearTimeout(this.hotspotHideTimer);
        this.openHotspot(hotspot);
      }, { signal });
      hotspot.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'touch' || this.isMobileHotspotLayout()) return;
        this.scheduleHotspotClose(hotspot);
      }, { signal });
      hotspot.addEventListener('focusin', () => this.openHotspot(hotspot), { signal });
      hotspot.addEventListener(
        'focusout',
        () => {
          requestAnimationFrame(() => {
            if (!hotspot.contains(document.activeElement) && hotspot.dataset.locked !== 'true') {
              this.closeHotspot(hotspot);
            }
          });
        },
        { signal },
      );
      trigger.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          event.stopPropagation();
          window.clearTimeout(this.hotspotHideTimer);
          if (this.isMobileHotspotLayout()) {
            const shouldOpen = this.trayPanel !== panel;
            this.closeAllHotspots();
            hotspot.dataset.locked = String(shouldOpen);
            if (shouldOpen) this.openHotspot(hotspot);
            return;
          }

          const shouldOpen = hotspot.dataset.locked !== 'true';
          this.closeAllHotspots();
          hotspot.dataset.locked = String(shouldOpen);
          if (shouldOpen) this.openHotspot(hotspot);
        },
        { signal },
      );
      trigger.addEventListener(
        'keydown',
        (event) => {
          if (event.key !== 'Escape') return;
          hotspot.dataset.locked = 'false';
          this.closeHotspot(hotspot);
          trigger.focus();
        },
        { signal },
      );
    });

    document.addEventListener(
      'pointerdown',
      (event) => {
        if (this.contains(event.target) || this.tray?.contains(event.target)) return;
        this.closeAllHotspots();
      },
      { signal },
    );
  }

  openHotspot(hotspot) {
    const trigger = hotspot.querySelector('[data-sybil-hotspot-trigger]');
    const panel = hotspot.sybilPanel ?? hotspot.querySelector('[data-sybil-hotspot-panel]');
    if (!(panel instanceof HTMLElement)) return;

    this.hotspots
      ?.filter((candidate) => candidate !== hotspot && candidate.dataset.open === 'true')
      .forEach((candidate) => this.closeHotspot(candidate));
    if (this.isMobileHotspotLayout()) this.movePanelToTray(hotspot, panel);
    hotspot.dataset.open = 'true';
    trigger?.setAttribute('aria-expanded', 'true');
    panel.setAttribute('aria-hidden', 'false');
    panel.removeAttribute('inert');
    requestAnimationFrame(() => this.positionHotspotPanel(hotspot));
  }

  closeHotspot(hotspot) {
    const trigger = hotspot.querySelector('[data-sybil-hotspot-trigger]');
    const panel = hotspot.sybilPanel ?? hotspot.querySelector('[data-sybil-hotspot-panel]');
    hotspot.dataset.open = 'false';
    trigger?.setAttribute('aria-expanded', 'false');
    panel?.setAttribute('aria-hidden', 'true');
    panel?.setAttribute('inert', '');
    if (panel === this.trayPanel) this.restoreTrayPanel();
  }
}

if (!customElements.get('sybil-hero')) {
  customElements.define('sybil-hero', SybilHero);
}
